import React, {useContext, useMemo} from 'react'

/**
 * Permission flags consumed by the storage-levels material orders UI.
 *
 * The storage-levels feature itself does NOT check permissions. In Suite (lean roles)
 * every action is allowed, which is the default when no provider is present.
 *
 * Host apps that still enforce permissions (e.g. HCEM) can wrap the feature with
 * `MaterialOrderPermissionsProvider` and pass the resolved permission values in.
 */
export interface MaterialOrderPermissions {
  canViewMaterialOrders: boolean
  canChangeMaterialOrders: boolean
  canViewComments: boolean
  canEditComments: boolean
  canViewDocuments: boolean
  canAddDocuments: boolean
  canDeleteDocuments: boolean
}

const ALL_ALLOWED: MaterialOrderPermissions = {
  canViewMaterialOrders: true,
  canChangeMaterialOrders: true,
  canViewComments: true,
  canEditComments: true,
  canViewDocuments: true,
  canAddDocuments: true,
  canDeleteDocuments: true
}

const MaterialOrderPermissionsContext = React.createContext<MaterialOrderPermissions>(ALL_ALLOWED)

/**
 * Returns the current material order permissions. Defaults to "all allowed" (lean roles)
 * when no `MaterialOrderPermissionsProvider` is present.
 */
export const useMaterialOrderPermissions = (): MaterialOrderPermissions =>
  useContext(MaterialOrderPermissionsContext)

type MaterialOrderPermissionsProviderProps = {
  children: React.ReactNode
  /** Permission overrides. Any omitted flag defaults to allowed. */
  permissions?: Partial<MaterialOrderPermissions>
}

export const MaterialOrderPermissionsProvider: React.FC<MaterialOrderPermissionsProviderProps> = ({
  children,
  permissions
}) => {
  const value = useMemo<MaterialOrderPermissions>(
    () => ({...ALL_ALLOWED, ...permissions}),
    [permissions]
  )

  return (
    <MaterialOrderPermissionsContext.Provider value={value}>
      {children}
    </MaterialOrderPermissionsContext.Provider>
  )
}
